import { useQueries, useQuery, useSuspenseQueries } from '@tanstack/react-query'
import { MetadataModel } from 'ApiClient/metadata/types'
import { useMemo, useRef } from 'react'

import { MetadataName, MetadataValue, metadataQueries } from 'Common/api/metadataQueries'

function useStableMetadataMap(
    names: readonly string[],
    values: readonly unknown[],
): Record<string, unknown> {
    const cacheRef = useRef<{
        names: readonly string[]
        values: readonly unknown[]
        map: Record<string, unknown>
    } | null>(null)

    const cached = cacheRef.current
    if (
        cached &&
        cached.names.length === names.length &&
        cached.values.length === values.length &&
        cached.names.every((name, index) => name === names[index]) &&
        cached.values.every((value, index) => value === values[index])
    ) {
        return cached.map
    }

    const map = Object.fromEntries(names.map((name, index) => [name, values[index]]))
    cacheRef.current = { names: [...names], values: [...values], map }
    return map
}

export function useMetadata<const K extends readonly MetadataName[]>(
    names: K,
): { [P in K[number]]: MetadataValue<P> } {
    const results = useSuspenseQueries({
        queries: names.map((name) => metadataQueries.byName(name)),
    })

    return useStableMetadataMap(
        names,
        results.map((result) => result.data),
    ) as {
        [P in K[number]]: MetadataValue<P>
    }
}

export type MetadataBundle<K extends readonly MetadataName[] = []> = {
    [P in keyof MetadataModel]-?: NonNullable<MetadataModel[P]>
} & {
    [P in K[number]]: MetadataValue<P>
}

export function useMetadataBundle<const K extends readonly MetadataName[]>(
    names: K,
): MetadataBundle<K> {
    const md = useMetadata(names) as Record<string, unknown>
    return useMemo(
        () =>
            ({
                ...md,
                subGroups: md.subgroups,
                listingCodes: md.governingCodes,
                bonnierRightsCategory: md.agencyRightsCategory,
                bonnierRightsSubCategory: md.agencyRightsSubCategory,
            }) as unknown as MetadataBundle<K>,
        [md],
    )
}

export const assetMetadataNames = [
    'assetTypes',
    'assetContractTypes',
] as const satisfies readonly MetadataName[]

export const sharedMetadataNames = [
    'publishingHouses',
    'publishingHouseGroups',
    'contactRoles',
    'brands',
    'seasonPeriods',
    'bindingCodes',
    'publishingLists',
] as const satisfies readonly MetadataName[]

export function usePrefetchSharedMetadata(): void {
    useQuery(metadataQueries.serviceBundle())
    useQueries({
        queries: sharedMetadataNames.map((name) => metadataQueries.byName(name)),
    })
}
